import React from 'react';
import { jsPDF } from 'jspdf';
import moment from 'moment';
import { Application, Badge, Company, Contact } from '../interfaces/users.interface';
import { Workflow } from '../interfaces/workflow.interface';
import { workflowLabels } from './workflow';
import getAuthSession from './auth';
import axios from 'axios';
import { Typography } from '@mui/material';
import { AssessmentQuestionStatusById, QuestionStatusById } from '../app/store/questionnaireAnswerSlice';
import { REMEDIATION_OPTIONS, REMEDIATION_VERIFICATION_STATUS } from './surveyHelpers';

// Badge labels
export const BLUE_BADGE_LABEL = 'tpn_self_reported';
export const GOLD_BADGE_LABEL = 'tpn_assessed';
export const SILVER_BADGE_LABEL = 'tpn_assessed_remediation_plans_submitted';
export const GOLD_NEXT_GEN_BADGE_LABEL = 'tpn_assessed_best_practices_verified';
export const GOLD_STAR_BADGE_LABEL = 'tpn_assessed_all_questions_verified';
export const CERTIFICATION_BADGE_LABEL = 'non_tpn_self_reported';

export const BadgeHierarchy = {
  'non_tpn_self_reported': 0,
  [BLUE_BADGE_LABEL]: 1,
  [GOLD_BADGE_LABEL]: 2,
  [SILVER_BADGE_LABEL]: 3,
  [GOLD_NEXT_GEN_BADGE_LABEL]: 4,
  [GOLD_STAR_BADGE_LABEL]: 5,
};


export const AssessmentBadgeLabels = [
  GOLD_BADGE_LABEL,
  SILVER_BADGE_LABEL,
  GOLD_NEXT_GEN_BADGE_LABEL,
  GOLD_STAR_BADGE_LABEL,
];
interface GenerateGoldShieldProps {
  company: Company;
  workflow: Workflow;
  site?: Contact;
}

type BadgeErrorResponse = {
  details: {
    questionsAnsweredByCerts: string[];
    questionsMissingAllAnswers: string[];
    questionsMissingAnyAnswers: string[];
  };
  error: string;
};

type BadgeAwardResponse = {
  message: string;
};

function isBadgeErrorData(data: unknown): data is BadgeErrorResponse {
  if (!data || typeof data !== 'object') return false;

  const d = data as any;
  return (
    typeof d.error === 'string' &&
    d.details &&
    typeof d.details === 'object' &&
    Array.isArray(d.details.questionsAnsweredByCerts) &&
    Array.isArray(d.details.questionsMissingAllAnswers) &&
    Array.isArray(d.details.questionsMissingAnyAnswers)
  );
}

export interface BadgeMeta {
  label: string;
  image: { src: string; alt: string } | null;
  tooltip: JSX.Element;
  expirationDate: string | null;
  expired: boolean;
  isNextGen?: boolean;
  count: number;
  count_expired: number;
}
// Badge images
export const BLUE_BADGE_IMAGE = `${process.env.PUBLIC_URL}/assets/TPN_BlueLogoTransparent_1280.png`;
export const BLUE_NEXT_GEN_BADGE_IMAGE = `${process.env.PUBLIC_URL}/assets/TPN_NextGen_BlueLogoTransparent_1280.png`;
export const SILVER_BADGE_IMAGE = `${process.env.PUBLIC_URL}/assets/TPN_SilverTransparent_1280.png`;
export const GOLD_BADGE_IMAGE = `${process.env.PUBLIC_URL}/assets/TPN_GoldLogoTransparent_1280.png`;
export const GOLD_NEXT_GEN_BADGE_IMAGE = `${process.env.PUBLIC_URL}/assets/TPN_GoldNextGenTransparent_1280.png`;
export const GOLD_STAR_BADGE_IMAGE = `${process.env.PUBLIC_URL}/assets/TPN_GoldStarTransparent_1280.png`;

export const GOLD_STATUS_IMAGE = '/assets/GoldShieldStatus.png';
export const SILVER_STATUS_IMAGE = '/assets/SilverShieldStatus.png';
export const GOLD_NEXT_GEN_STATUS_IMAGE = '/assets/GoldNextGenShieldStatus.png';
export const GOLD_STAR_STATUS_IMAGE = '/assets/GoldStarShieldStatus.png';

export const ASSESSMENT_BADGE_TITLES = new Set([
  GOLD_BADGE_LABEL,
  SILVER_BADGE_LABEL,
  GOLD_NEXT_GEN_BADGE_LABEL,
  GOLD_STAR_BADGE_LABEL,
]);

export const STATUS_LABEL_IMAGE_MAP: Record<string, string> = {
  tpn_assessed: GOLD_STATUS_IMAGE,
  tpn_assessed_remediation_plans_submitted: SILVER_STATUS_IMAGE,
  tpn_assessed_best_practices_verified: GOLD_NEXT_GEN_STATUS_IMAGE,
  tpn_assessed_all_questions_verified: GOLD_STAR_STATUS_IMAGE,
};

export const BADGE_LABEL_IMAGEE_MAP: Record<string, string> = {
  tpn_self_reported: BLUE_BADGE_IMAGE,
  tpn_assessed: GOLD_BADGE_IMAGE,
  tpn_assessed_remediation_plans_submitted: SILVER_BADGE_IMAGE,
  tpn_assessed_best_practices_verified: GOLD_NEXT_GEN_BADGE_IMAGE,
  tpn_assessed_all_questions_verified: GOLD_STAR_BADGE_IMAGE,
};

export const badgeFileMap: Record<string, { image: string; filename: string }> = {
  [BLUE_BADGE_LABEL]: {
    image: BLUE_BADGE_IMAGE,
    filename: 'TPN_Blue_Badge.png',
  },
  [GOLD_BADGE_LABEL]: {
    image: `${process.env.PUBLIC_URL}/assets/TPN_GoldLogoTransparent_1280.png`,
    filename: 'TPN_Gold_Badge.png',
  },
  [SILVER_BADGE_LABEL]: {
    image: `${process.env.PUBLIC_URL}/assets/TPN_SilverTransparent_1280.png`,
    filename: 'TPN_Silver_Badge.png',
  },
  [GOLD_NEXT_GEN_BADGE_LABEL]: {
    image: `${process.env.PUBLIC_URL}/assets/TPN_GoldNextGenTransparent_1280.png`,
    filename: 'TPN_Gold_NextGen_Badge.png',
  },
  [GOLD_STAR_BADGE_LABEL]: {
    image: `${process.env.PUBLIC_URL}/assets/TPN_GoldStarTransparent_1280.png`,
    filename: 'TPN_Gold_Star_Badge.png',
  },
};

export interface BadgeImageData {
  src: string;
  alt: string;
  label: string;
}

export const BADGE_IMAGE_DATA_MAP: Record<string, BadgeImageData> = {
  gold: {
    src: GOLD_BADGE_IMAGE,
    alt: 'Gold Shield Logo',
    label: 'Assessment Report',
  },
  blue: {
    src: BLUE_BADGE_IMAGE,
    alt: 'Blue Shield Logo',
    label: 'Questionnaire Report',
  },
  blueNextGen: {
    src: BLUE_NEXT_GEN_BADGE_IMAGE,
    alt: 'Blue Shield Logo',
    label: 'Questionnaire Report',
  },
  silver: {
    src: SILVER_BADGE_IMAGE,
    alt: 'Silver Shield Logo',
    label: 'Assessment Report',
  },
  goldNextGen: {
    src: GOLD_NEXT_GEN_BADGE_IMAGE,
    alt: 'Gold(NextGen) Shield Logo',
    label: 'Assessment Report',
  },
  goldStar: {
    src: GOLD_STAR_BADGE_IMAGE,
    alt: 'Gold Star Shield Logo',
    label: 'Assessment Report',
  },
};

export const initialBadgeMeta: Record<string, Omit<BadgeMeta, 'expirationDate'>> = {
  tpn_self_reported: {
    label: 'Blue',
    expired: false,
    count: 0,
    count_expired: 0,
    image: {
      src: BLUE_BADGE_IMAGE,
      alt: 'Blue Shield Logo',
    },
    tooltip: (
      <Typography variant="body2">
        Blue
      </Typography>
    ),
  },
  tpn_assessed: {
    label: 'Gold (Classic)',
    expired: false,
    count: 0,
    count_expired: 0,
    image: {
      src: GOLD_BADGE_IMAGE,
      alt: 'Gold (Classic) Shield Logo',
    },
    tooltip: (
      <Typography variant="body2">
        Gold (Classic)
      </Typography>
    ),
  },
  tpn_assessed_remediation_plans_submitted: {
    label: 'Silver',
    expired: false,
    count: 0,
    count_expired: 0,
    image: {
      src: SILVER_BADGE_IMAGE,
      alt: 'Silver Shield Logo',
    },
    tooltip: (
      <Typography variant="body2">
        Silver
      </Typography>
    ),
  },
  tpn_assessed_best_practices_verified: {
    label: 'Gold (NextGen)',
    expired: false,
    count: 0,
    count_expired: 0,
    image: {
      src: GOLD_NEXT_GEN_BADGE_IMAGE,
      alt: 'Gold (NextGen) Shield Logo',
    },
    tooltip: (
      <Typography variant="body2">
        Gold (NextGen)
      </Typography>
    ),
  },
  tpn_assessed_all_questions_verified: {
    label: 'Gold Star',
    expired: false,
    count: 0,
    count_expired: 0,
    image: {
      src: GOLD_STAR_BADGE_IMAGE,
      alt: 'Gold Star Shield Logo',
    },
    tooltip: (
      <Typography variant="body2">
        Gold Star
      </Typography>
    ),
  },
};

export const eligibleForBlueBadge = async (workflowId: number): Promise<BadgeErrorResponse | BadgeAwardResponse | undefined> => {
  const authSession = await getAuthSession();
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_API}/badges/award-tpn-self-reported/${workflowId}/`,
      {
        headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
      },
    );
    return response.data as BadgeAwardResponse;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data;

      if (isBadgeErrorData(data)) {
        return data;
      }
    }

    return;
  }
};

export const eligibleForNextGenGoldBadge = async (workflowId: number) => {
  const authSession = await getAuthSession();
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_API}/badges/award-tpn-best-practices-verified/${workflowId}/`,
      {
        headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
      },
    );
    return response;
  } catch (error) {
    console.error('Error checking eligibility for NextGen Gold badge:', error);
    return;
  }
};

export const awardNextGenGoldBadge = async (workflowId: number): Promise<Badge | null> => {
  const authSession = await getAuthSession();
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_API}/badges/award-tpn-best-practices-verified/${workflowId}/`,
      {},
      { headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` } },
    );
    return response.data;
  } catch (error) {
    console.error('Error awarding NextGen Gold badge:', error);
    return null;
  }
};

export const nextGenEligibilityAwardCheck = async (wfId: number): Promise<number[]> => {
  try {
    const nextGenEligible = await eligibleForNextGenGoldBadge(wfId);
    if (!nextGenEligible) return [];

    const badge = await awardNextGenGoldBadge(wfId);
    return typeof badge?.id === 'number' ? [badge.id] : [];
  } catch (err) {
    console.error('Verification eligibility check error', err);
    return [];
  }
};

export  const getBadgeRank = (badges: Badge[] = []) => {
  const nonExpiredBadges = badges.filter((b) => moment(b.expirationDate).isAfter(moment()));
  if (!nonExpiredBadges.length) return -1;

  const badgeRank = [BLUE_BADGE_LABEL, GOLD_BADGE_LABEL, SILVER_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL];
  return Math.max(...nonExpiredBadges.map(b => badgeRank.indexOf(b.title)));
};

export const determineVerificationStatus = (
  assessmentQuestionsStatus: AssessmentQuestionStatusById,
  questionStatus: QuestionStatusById,
): 
{ qualifyingGold: boolean;
  qualifyingGoldStar: boolean
} | null => {
  let qualifyingGold = true, qualifyingGoldStar = true;
  for (const assQ in assessmentQuestionsStatus) {
    const { status, remediationQuestionAnswers, question } = assessmentQuestionsStatus[assQ];
    const qs = questionStatus[question as number];
    if (status === 'assessorApproved' || qs.status === 'hide') continue;

    if (!remediationQuestionAnswers) return null;

    const allControlsVerified = remediationQuestionAnswers.every((rqa) => {
      const valid = rqa.remediationPlan === REMEDIATION_OPTIONS.remediated && rqa.verificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed;
      // QA testing... Can be removed for prod deployment and direct return the above
      if (!valid) {
        console.warn('Question is not verified:', remediationQuestionAnswers,  qs);
      }
      return valid;
    });


    if (!allControlsVerified) {
      if (!qs?.question) continue;
    
      if (qs.question.isBestPractice) {
        // Ineligible for Gold and Gold Star
        return null;
      } else {
        // Ineligible for Gold Plus
        qualifyingGoldStar = false;
      }
    }
  }

  return { qualifyingGold, qualifyingGoldStar };
};


export const eligibleForGoldStarBadge = async (workflowId: number) => {
  const authSession = await getAuthSession();
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_API}/badges/award-tpn-assessed-all-questions-verified/${workflowId}/`,
      {
        headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
      },
    );
    return response;
  } catch (error) {
    console.error('Error checking eligibility for NextGen Gold badge:', error);
    return;
  }
};

export const awardGoldStarBadge = async (workflowId: number): Promise<Badge | null> => {
  const authSession = await getAuthSession();
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_API}/badges/award-tpn-assessed-all-questions-verified/${workflowId}/`,
      {},
      { headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` } },
    );
    return response.data;

  } catch (error) {
    console.error('Error awarding Gold Star badge:', error);
    return null;
  }
};

export const selectBadgeFromArray = (badges?: Badge[], allowExpired = false) => {
  if (!badges || badges.length === 0) return undefined;
  const today = moment().unix();
  const filteredBadges = badges.filter(badge => badge && (!allowExpired ? moment(badge.expirationDate).unix() > today : true)).sort((a, b) => moment(b.expirationDate).unix() - moment(a.expirationDate).unix());
  if (filteredBadges.find(b => b.title === GOLD_STAR_BADGE_LABEL)) return filteredBadges.filter(badge => badge.title === GOLD_STAR_BADGE_LABEL)[0];
  if (filteredBadges.find(b => b.title === GOLD_NEXT_GEN_BADGE_LABEL)) return filteredBadges.filter(badge => badge.title === GOLD_NEXT_GEN_BADGE_LABEL)[0];
  if (filteredBadges.find(b => b.title === SILVER_BADGE_LABEL)) return filteredBadges.filter(badge => badge.title === SILVER_BADGE_LABEL)[0];
  if (filteredBadges.find(b => b.title === GOLD_BADGE_LABEL)) return filteredBadges.filter(badge => badge.title === GOLD_BADGE_LABEL)[0];
  if (filteredBadges.find(b => b.title === BLUE_BADGE_LABEL)) return filteredBadges.filter(badge => badge.title === BLUE_BADGE_LABEL)[0];
  return filteredBadges[0];
};

const AssessmentBadgeRank = {
  [GOLD_STAR_BADGE_LABEL]: 4,
  [GOLD_NEXT_GEN_BADGE_LABEL]: 3,
  [SILVER_BADGE_LABEL]: 2,
  [GOLD_BADGE_LABEL]: 1,
};

type BadgeTitle = keyof typeof AssessmentBadgeRank;

const getHighestRankAssessmentBadgeLabel = (badges: Badge[]): BadgeTitle => {
  let rank = 1; // default GOLD (Classic)
  return badges.reduce<BadgeTitle>((acc, b) => {
    const title = b.title as BadgeTitle;
    if (AssessmentBadgeRank[title] && AssessmentBadgeRank[title] > rank) {
      rank = AssessmentBadgeRank[title];
      acc = title;
    }
    return acc;
  }, GOLD_BADGE_LABEL);
};

// fetches most recent badge of a specific type from a list of workflows
export const recentBadgeByType = (list: Workflow[], type: string[]) => {
  return list.reduce((acc, wf) => {
    const isAssessmentLookup = type.length > 1 ? true : false;
    const lookupValue = isAssessmentLookup ? getHighestRankAssessmentBadgeLabel(wf.badges as Badge[]) : type[0];
    const candidates = (wf.badges as (Badge | number)[])?.filter((b): b is Badge => typeof b !== 'number' && b.title && b.title === lookupValue);
    if (!candidates || candidates.length === 0) return acc;
    candidates.sort((a, b) => moment(b.awardedDate).unix() - moment(a.awardedDate).unix());
    if (!acc.badgeDate || moment(acc.badgeDate).unix() < moment(candidates[0].awardedDate).unix()) {
      acc.badgeDate = candidates[0].awardedDate;
      acc.badge = candidates[0];
      acc.version = workflowLabels(wf.workflowType);
    }
    return acc;
  }, {
    badgeDate: '',
    badge: undefined as Badge | undefined,
    version: '',
  });
};

export const getHighestAchievement = (
  workflows: Workflow[] | undefined,
  badgeTitleArr: (
    typeof BLUE_BADGE_LABEL |
    typeof GOLD_BADGE_LABEL |
    typeof SILVER_BADGE_LABEL |
    typeof GOLD_NEXT_GEN_BADGE_LABEL |
    typeof GOLD_STAR_BADGE_LABEL |
    typeof CERTIFICATION_BADGE_LABEL
  )[],
  isApp?: boolean,
) => {
  if (!workflows || workflows.length === 0) return;

  // Apps may have multiple workflows version to choose from
  const lookupWorkflowBadges = isApp ?
    workflows.flatMap(wf => wf.badges as Badge[]) :
    workflows[0].badges as Badge[];
  const isAssessmentLookup = badgeTitleArr.length > 1 ? true : false;
  const lookupValue = isAssessmentLookup ? getHighestRankAssessmentBadgeLabel(lookupWorkflowBadges) : badgeTitleArr[0];
  const candidates = (lookupWorkflowBadges)?.filter((b): b is Badge => typeof b !== 'number' && b.title && b.title === lookupValue);

  if (candidates.length === 0) return;

  // Sort candidates by most recent awarded date
  candidates.sort((a, b) => moment(b.awardedDate).unix() - moment(a.awardedDate).unix());

  const workflowType = isApp
    ? workflows.find(wf => (wf.badges as Badge[])?.includes(candidates[0] ))?.workflowType || ''
    : workflows[0].workflowType || '';

  return {
    badgeDate: candidates[0].awardedDate,
    badge: candidates[0],
    version: workflowLabels(workflowType),
  };
};

const fetchBadgeTitle = (badges?: Badge[], badgeTitle?: string, targetTitle?: string, color?: string) => {
  if (!badges || badges.length === 0) return undefined;
  const badge = badges.find(b => b.title === targetTitle);
  const today = moment().unix();
  if (!badge) return undefined;
  const exp = moment(badge.expirationDate).unix() > today ? 'Expires ' : 'Expired ';
  const version = badgeTitle ? badgeTitle.split(' ')[1] : '';
  return `${color} - ${version} ${exp}${moment(badge.expirationDate).format('MMM D, YYYY')}`;
};

export const fetchGoldBadgeLabel = (badges?: Badge[], badgeTitle?: string, mostRecentGoldBadgeLabel?: string) => {
  return fetchBadgeTitle(badges, badgeTitle, mostRecentGoldBadgeLabel || GOLD_BADGE_LABEL, initialBadgeMeta[mostRecentGoldBadgeLabel || GOLD_BADGE_LABEL]?.label);
};

export const fetchBlueBadgeLabel = (badges?: Badge[], badgeTitle?: any) => {
  return fetchBadgeTitle(badges, badgeTitle, BLUE_BADGE_LABEL, 'Blue');
};

export const statusFileName = (badgeLabel: string, company: Company, site?: Contact, applicationName?: string) => {
  const shiedColor = badgeLabel === GOLD_NEXT_GEN_BADGE_LABEL
    ? 'Gold_NextGen'
    : badgeLabel === GOLD_STAR_BADGE_LABEL
      ? 'Gold_Star'
      : badgeLabel === SILVER_BADGE_LABEL
        ? 'Silver'
        : 'Gold';

  return `${company?.name || 'Company'}_${site?.name || applicationName}_TPN_${shiedColor}_Shield_Status.pdf`;
};

export const downloadWithWhiteBackground = (imgSrc: string, fileName: string) => {
  const image = new Image();
  image.crossOrigin = 'Anonymous';
  image.src = imgSrc;

  image.onload = () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to match image
    canvas.width = image.width;
    canvas.height = image.height;

    // Fill the background with white
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw the image on top of the white background
    context.drawImage(image, 0, 0);

    // Generate the image and download
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = fileName;
    link.click();
  };
};

export const highestBadgeImageData = (badges: string[]) => {
  if (badges.includes(GOLD_STAR_BADGE_LABEL)) {
    return BADGE_IMAGE_DATA_MAP.goldStar;
  }
  if (badges.includes(GOLD_NEXT_GEN_BADGE_LABEL)) {
    return BADGE_IMAGE_DATA_MAP.goldNextGen;
  }
  if (badges.includes(SILVER_BADGE_LABEL)) {
    return BADGE_IMAGE_DATA_MAP.silver;
  }
  if (badges.includes(GOLD_BADGE_LABEL)) {
    return BADGE_IMAGE_DATA_MAP.gold;
  }
  return null;
};

export const generateGoldShield = ({
  company,
  site,
  workflow,
}: GenerateGoldShieldProps) => {
  const highestRankingBadgeLabel = getHighestRankAssessmentBadgeLabel(workflow.badges as Badge[]);

  const background = new Image();
  background.src = STATUS_LABEL_IMAGE_MAP[highestRankingBadgeLabel] || GOLD_STATUS_IMAGE;

  const isGoldClassic = highestRankingBadgeLabel === GOLD_BADGE_LABEL;

  background.onload = function () {
    let pageW: number, pageH: number, bgW: number, bgH: number, bgX: number, bgY: number;

    if (isGoldClassic) {
      const padding = 25;
      pageW = background.width + padding * 2;
      pageH = background.height + padding * 2;

      bgW = background.width;
      bgH = background.height;
      bgX = padding;
      bgY = padding;
    } else {
      const PAGE_SCALE = 0.85;
      const BG_SCALE   = 0.91;

      pageW = Math.round(background.width  * PAGE_SCALE);
      pageH = Math.round(background.height * PAGE_SCALE);

      bgW = Math.round(background.width  * BG_SCALE);
      bgH = Math.round(background.height * BG_SCALE);

      bgX = Math.round((pageW - bgW) / 2);
      bgY = Math.round((pageH - bgH) / 2);
    }

    const canvas = document.createElement('canvas');
    canvas.width  = pageW;
    canvas.height = pageH;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, pageW, pageH);
    ctx.drawImage(background, bgX, bgY, bgW, bgH);
    ctx.textAlign = 'center';

    const x = canvas.width / 2;
    const maxWidth = 870;

    // Helper to apply ellipsis if text is too wide
    function ellipsize(ctxProp: CanvasRenderingContext2D, text: string, maxW: number) {
      const ellipse = '…';
      if (ctxProp.measureText(text).width <= maxW) return text;
      let low = 0, high = text.length;
      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        const slice = text.slice(0, mid) + ellipse;
        if (ctxProp.measureText(slice).width <= maxW) low = mid;
        else high = mid - 1;
      }
      return text.slice(0, low) + ellipse;
    }

    // Helper to wrap text with ellipsis on last line if needed
    function wrapTextEllipsis(
      ctxProp: CanvasRenderingContext2D,
      text: string,
      maxW: number,
      maxLines: number,
    ): string[] {
      const words = (text || '').split(/\s+/); // split on whitespace
      const lines: string[] = [];
      let line = '';

      for (let i = 0; i < words.length; i++) {
        const tryLine = (line ? line + ' ' : '') + words[i];
        if (ctxProp.measureText(tryLine).width <= maxW) {
          line = tryLine;
        } else {
          if (!line) {
            lines.push(ellipsize(ctxProp, words[i], maxW));
          } else {
            lines.push(line);
            if (lines.length === maxLines - 1) {
              const remaining = [words[i], ...words.slice(i + 1)].join(' ');
              lines.push(ellipsize(ctxProp, remaining, maxW));
              return lines;
            }
            line = words[i];
          }
        }
        if (lines.length === maxLines) return lines;
      }

      if (line) {
        if (lines.length === maxLines - 1 && ctxProp.measureText(line).width > maxW) {
          lines.push(ellipsize(ctxProp, line, maxW));
        } else if (lines.length < maxLines) {
          lines.push(line);
        }
      }
      return lines.slice(0, maxLines);
    }

    if (!workflow?.badges?.length) return;
    const badges = workflow.badges as Badge[];
    const wfCompDate = badges.find((b) => typeof b === 'object' && b.title === highestRankingBadgeLabel);
    const expirationDate = wfCompDate && typeof wfCompDate === 'object' ? moment(wfCompDate.expirationDate).format('MMM D, YYYY') : '';
    const completedDate  = wfCompDate && typeof wfCompDate === 'object' ? moment(wfCompDate.awardedDate).format('MMM D, YYYY') : '';

    let serviceProviderName = company?.name + '' || '';

    const siteName = site?.name;
    const locationName = company.locations.find(l => l.id === workflow?.site)?.name;
    const applicationName = workflow?.application ? (workflow.application as Application).name : '';
    const siteApp = siteName || locationName || applicationName;

    const h1LineHeight = 70;
    const h2LineHeight = 70;
    const addrLineStep = 50;
    const cityStep = 45;
    const gapAfterSP = 5;
    const gapBeforeDates = 65;

    // SP name
    ctx.font = '700 60px Montserrat';
    ctx.fillStyle = '#1c2a42';
    const spLines = wrapTextEllipsis(ctx, serviceProviderName, maxWidth, 2);

    // Site/App name
    let saLines: string[] = [];
    if (serviceProviderName.trim().toLowerCase() !== (siteApp || '').trim().toLowerCase()) {
      ctx.font = '700 55px Montserrat';
      ctx.fillStyle = '#1c2a42';
      saLines = wrapTextEllipsis(ctx, siteApp || '', maxWidth, 2);
    }

    let addressLines: string[] = [];
    const hasSiteBlock = Boolean(site);
    if (hasSiteBlock) {
      ctx.font = '400 42px Montserrat';
      ctx.fillStyle = '#283352';
      addressLines = wrapTextEllipsis(ctx, `${site?.address}`, maxWidth, 3);
    }

    // Total block height
    let totalBlockH = 0;    
    totalBlockH += spLines.length * h1LineHeight + gapAfterSP;
    totalBlockH += saLines.length * h2LineHeight;

    if (hasSiteBlock) {
      totalBlockH += addressLines.length * addrLineStep;
      totalBlockH += cityStep;
      totalBlockH += gapBeforeDates;
    } else {
      totalBlockH += 45 + gapBeforeDates;
    }

    const dateSectionTop = 1050;

    // Starting y to center block above dates section
    let y = dateSectionTop - totalBlockH;

    // Safe starting y
    const minY = isGoldClassic ? 937 : 760;
    if (y < minY) y = minY;

    ctx.textAlign = 'center';

    // SP
    ctx.font = '700 60px Montserrat';
    ctx.fillStyle = '#1c2a42';
    for (let i = 0; i < spLines.length; i++) ctx.fillText(spLines[i], x, y + i * h1LineHeight);
    y += spLines.length * h1LineHeight + gapAfterSP;

    // Site/App
    if (saLines.length) {
      ctx.font = '600 50px Montserrat';
      ctx.fillStyle = '#1c2a42';
      for (let i = 0; i < saLines.length; i++) ctx.fillText(saLines[i], x, y + i * h2LineHeight);
      y += saLines.length * h2LineHeight;
    }

    if (hasSiteBlock) {
      ctx.font = '400 42px Montserrat';
      ctx.fillStyle = '#283352';
      for (let i = 0; i < addressLines.length && i < 3; i++) {
        ctx.fillText(addressLines[i], x, y + i * 45);
      }
      y += addressLines.length * addrLineStep;

      // City, State, Zip
      ctx.font = '500 30px Montserrat';
      ctx.fillText(`${site?.city}, ${site?.state} ${site?.zipcode}`, x, y);
      y += cityStep;

      // Country
      ctx.font = '600 32px Montserrat';
      ctx.fillText(`${site?.country}`, x, y);
    } else if (typeof workflow?.version !== 'number' && typeof workflow?.version !== 'string' && workflow?.version?.value) {
      // Version
      ctx.font = '700 40px Montserrat';
      ctx.fillStyle = '#2a3a52';
      ctx.fillText(`${workflow.version?.value}`, x, y);
    }
    y += gapBeforeDates;

    ctx.textAlign = 'center';
    if (y < dateSectionTop) y = dateSectionTop;

    const containerW = Math.min(Math.round(pageW * 0.65), 800);
    const colW = Math.floor(containerW / 2);
    const leftCx = x - Math.floor(colW / 2);
    const rightCx = x + Math.floor(colW / 2);

    ctx.font = '700 40px Montserrat';
    ctx.fillStyle = '#1c2a42';
    ctx.fillText('Completed Date',  leftCx, y);
    ctx.fillText('Expiration Date', rightCx, y);

    const dateGap = 70;
    ctx.font = '400 45px Montserrat';
    ctx.fillStyle = '#283352';
    ctx.fillText(`${completedDate}`,  leftCx,  y + dateGap);
    ctx.fillText(`${expirationDate}`, rightCx, y + dateGap);

    y += dateGap + 75;

    // Export
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: [pageW, pageH],
    });
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, pageW, pageH);
    const fileName = statusFileName(highestRankingBadgeLabel, company, site, applicationName);
    doc.save(fileName);
  };
};


export const processVerificationAward = async (type: typeof GOLD_NEXT_GEN_BADGE_LABEL | typeof GOLD_STAR_BADGE_LABEL, workflowId: number): Promise<Badge | undefined> => {
  try {
    const badge = type === GOLD_NEXT_GEN_BADGE_LABEL
      ? await awardNextGenGoldBadge(workflowId)
      : await awardGoldStarBadge(workflowId);

    if (badge?.id) {
      return badge;
    }
  } catch (error) {
    console.error(`Error awarding ${type} badge:`, error);
  }
};

export async function awardBlueBadge(workflowId: number) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/badges/award-tpn-self-reported/${workflowId}/`,
    {},
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as Badge;
}